
import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import Overview from './pages/Overview';
import UsersAccess from './pages/UsersAccess';
import WorkspacesPage from './pages/Workspaces';
import OrganizationsPage from './pages/Organizations';
import AgentsWorkflows from './pages/AgentsWorkflows';
import ModelsTools from './pages/ModelsTools';
import DataIntegrations from './pages/DataIntegrations';
import MonitoringAudit from './pages/MonitoringAudit';
import SettingsPage from './pages/Settings';
import { NotificationsPage } from './pages/NotificationsPage';
import { AdminView, Workspace, User, Agent } from './types';
import { MOCK_WORKSPACES, MOCK_USERS, MOCK_AGENTS } from './constants';
import { apiUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';


const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<AdminView>(AdminView.OVERVIEW);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [agents] = useState<Agent[]>(MOCK_AGENTS);

  const { token, logout } = useAuth(); // Destructure logout

  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<{ id: string; name: string }>({ id: 'all', name: 'Agents' });

  useEffect(() => {
    const fetchUsers = async () => {
      if (!token) return;
      try {
        const response = await fetch(apiUrl('/api/auth/users'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401) {
          console.warn('Unauthorized fetchUsers request. Logging out...');
          logout();
          return;
        }

        if (response.ok) {
          const data = await response.json();
          console.log('Fetched users raw data:', data); // Debug log

          if (Array.isArray(data)) {
            const transformedUsers: User[] = data.map((u: any) => ({
              id: u.id,
              name: u.username || u.name || 'Unknown',
              email: u.email,
              role: (u.role as User['role']) || 'Viewer',
              status: u.is_active ? 'Active' : 'Inactive',
              lastLogin: u.created_at || '',
              workspaces: []
            }));
            setUsers(transformedUsers);
            console.log('Transformed users:', transformedUsers);
          } else {
            console.error('Fetched users data is not an array:', data);
          }
        } else {
          console.error('Failed to fetch users. Status:', response.status);
        }
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };

    const transformWorkspace = (ws: any): Workspace => ({
      ...ws,
      members: ws.members?.map((m: any) => ({
        userId: m.user_id || m.userId,
        role: m.role,
        workspaceId: m.workspace_id || m.workspaceId,
        joinedAt: m.joined_at || m.joinedAt
      })) || []
    });

    const fetchWorkspaces = async () => {
      try {
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(apiUrl('/api/workspaces/'), { headers });
        if (response.ok) {
          const data = await response.json();
          setWorkspaces(data.map(transformWorkspace));
        }
      } catch (error) {
        console.error('Failed to fetch workspaces:', error);
      }
    };

    fetchUsers();
    fetchWorkspaces();
  }, [token]);

  // Simple hash-based "routing" or state-based switching

  const renderContent = () => {
    // Helper to transform single workspace (needed here as well or move helper out)
    const transformWorkspace = (ws: any): Workspace => ({
      ...ws,
      members: ws.members?.map((m: any) => ({
        userId: m.user_id || m.userId,
        role: m.role,
        workspaceId: m.workspace_id || m.workspaceId,
        joinedAt: m.joined_at || m.joinedAt
      })) || []
    });

    switch (currentView) {
      case AdminView.OVERVIEW: return <Overview selectedAgent={selectedAgent} />;
      case AdminView.USERS_ACCESS: return <UsersAccess />;
      case AdminView.WORKSPACES:
        return (
          <WorkspacesPage
            workspaces={workspaces}
            users={users}
            agents={agents}
            onOpenWorkspace={(ws: Workspace) => {
              setSelectedWorkspace(ws);
              setCurrentView(AdminView.AGENTS_WORKFLOWS);
            }}
            onAddWorkspace={async (ws: Workspace) => {
              if (!token) return;
              try {
                console.log('Sending create workspace request:', ws);
                const response = await fetch(apiUrl('/api/workspaces/'), {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify(ws)
                });
                if (response.ok) {
                  const newWsRaw = await response.json();
                  console.log('Backend response payload (new workspace):', newWsRaw);
                  const newWs = transformWorkspace(newWsRaw);
                  setWorkspaces(prev => [...prev, newWs]);
                } else {
                  console.error('Failed to create workspace. Status:', response.status);
                  const errText = await response.text();
                  console.error('Error details:', errText);
                }
              } catch (error) {
                console.error('Failed to create workspace:', error);
              }
            }}
            onUpdateWorkspace={async (updatedWs: Workspace) => {
              if (!token) return;
              try {
                const response = await fetch(apiUrl(`/api/workspaces/${updatedWs.id}`), {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify(updatedWs)
                });
                if (response.ok) {
                  const savedWsRaw = await response.json();
                  const savedWs = transformWorkspace(savedWsRaw);
                  setWorkspaces(prev => prev.map(ws => ws.id === savedWs.id ? savedWs : ws));
                } else {
                  console.error('Failed to update workspace. Status:', response.status);
                }
              } catch (error) {
                console.error('Failed to update workspace:', error);
              }
            }}
            onDeleteWorkspace={async (id: string) => {
              if (!token) return;
              try {
                const response = await fetch(apiUrl(`/api/workspaces/${id}`), {
                  method: 'DELETE',
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                if (response.ok) {
                  setWorkspaces(workspaces.filter(ws => ws.id !== id));
                } else {
                  console.error('Failed to delete workspace. Status:', response.status);
                }
              } catch (error) {
                console.error('Failed to delete workspace:', error);
              }
            }}
          />
        );
      case AdminView.ORGANIZATIONS:
        return <OrganizationsPage />;
      case AdminView.AGENTS_WORKFLOWS:
        return (
          <AgentsWorkflows
            selectedWorkspace={selectedWorkspace}
            onResetFilter={() => setSelectedWorkspace(null)}
            onUpdateWorkspace={async (updatedWs: Workspace) => {
              if (!token) return;
              try {
                const response = await fetch(apiUrl(`/api/workspaces/${updatedWs.id}`), {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify(updatedWs)
                });
                if (response.ok) {
                  const savedWsRaw = await response.json();
                  // Helper to transform single workspace
                  const transformWorkspace = (ws: any): Workspace => ({
                    ...ws,
                    members: ws.members?.map((m: any) => ({
                      userId: m.user_id || m.userId,
                      role: m.role,
                      workspaceId: m.workspace_id || m.workspaceId,
                      joinedAt: m.joined_at || m.joinedAt
                    })) || []
                  });
                  const savedWs = transformWorkspace(savedWsRaw);
                  setWorkspaces(prev => prev.map(ws => ws.id === savedWs.id ? savedWs : ws));
                  setSelectedWorkspace(savedWs); // Update selected workspace as well
                } else {
                  console.error('Failed to update workspace. Status:', response.status);
                }
              } catch (error) {
                console.error('Failed to update workspace:', error);
              }
            }}
          />
        );
      case AdminView.MODELS_TOOLS: return <ModelsTools />;
      case AdminView.DATA_INTEGRATIONS: return <DataIntegrations />;
      case AdminView.MONITORING_AUDIT: return <MonitoringAudit />;
      case AdminView.SETTINGS: return <SettingsPage />;
      case AdminView.NOTIFICATIONS: return <NotificationsPage />;
      default: return <Overview />;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-300">
      {/* Sidebar */}
      <Sidebar
        activeView={currentView}
        onViewChange={setCurrentView}
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Topbar */}
        <TopBar onViewChange={setCurrentView} selectedAgent={selectedAgent} setSelectedAgent={setSelectedAgent} />

        {/* Main Content Area */}
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-7xl mx-auto animate-fadeIn">
            {renderContent()}
          </div>
        </main>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default App;
